import React, { useEffect } from 'react';
import { API_BASE_URL } from '@/lib/api';

interface ResourcePreloaderProps {
  criticalResources?: Array<{
    href: string;
    as: 'font' | 'image' | 'script' | 'style';
    type?: string;
    crossorigin?: 'anonymous' | 'use-credentials';
  }>;
  prefetchResources?: string[];
  dnsPrefetch?: string[];
  preconnect?: string[];
}

const ResourcePreloader: React.FC<ResourcePreloaderProps> = ({
  criticalResources = [],
  prefetchResources = [],
  dnsPrefetch = [],
  preconnect = [],
}) => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Preload critical resources
    criticalResources.forEach(resource => {
      // Validează că href-ul este same-origin sau relativ pentru a preveni asset leakage între domenii
      try {
        const resourceUrl = new URL(resource.href, window.location.href);
        if (resourceUrl.origin !== window.location.origin && !resource.href.startsWith('/')) {
          console.warn('⚠️ Skipping cross-origin preload:', resource.href);
          return;
        }
      } catch (e) {
        // Relative URL, OK - continuă procesarea
      }
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as;
      if (resource.type) link.type = resource.type;
      if (resource.crossorigin) link.crossOrigin = resource.crossorigin;
      document.head.appendChild(link);
    });

    // Prefetch resources - resolve API paths through API_BASE_URL so they
    // hit the backend, not the frontend origin (avoids 503 on Cloudflare Pages)
    prefetchResources.forEach(href => {
      let resolvedHref = href;
      const isApiPath = href.startsWith('/api/');

      if (isApiPath && API_BASE_URL) {
        const base = API_BASE_URL.replace(/\/api\/?$/, '');
        resolvedHref = `${base}${href}`;
      }

      if (!isApiPath) {
        try {
          const resourceUrl = new URL(resolvedHref, window.location.href);
          if (resourceUrl.origin !== window.location.origin && !resolvedHref.startsWith('/')) {
            return;
          }
        } catch (e) {
          // Relative URL, OK
        }
      }

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = resolvedHref;
      document.head.appendChild(link);
    });

    // DNS prefetch
    dnsPrefetch.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `//${domain}`;
      document.head.appendChild(link);
    });

    // Preconnect
    preconnect.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    // Cleanup function
    return () => {
      // Remove any dynamically added links if needed
      const addedLinks = document.querySelectorAll('link[data-preloader]');
      addedLinks.forEach(link => link.remove());
    };
  }, [criticalResources, prefetchResources, dnsPrefetch, preconnect]);

  return null; // This component doesn't render anything
};

export default ResourcePreloader;
